<template>
  <div
    class="product-grid -mx-2 mb-2 flex flex-wrap lg:-mx-2.5 xl:-mx-3"
    :class="{
      'mt-0 sm:mt-2': !props.hasSmallerTopPadding,
      '-mt-4': props.hasSmallerTopPadding,
      'product-grid-with-value-propositions': valuePropositionsPlacementIndex >= 0,
    }"
  >
    <template v-for="(product, index) in props.products" :key="index">
      <div class="product-card w-1/2 border-t border-border-light product-md:w-1/3 xl:w-1/4">
        <ProductCard :product="product" :lazy-loaded="index > 8" />
      </div>
      <!--      <ValuePropositions v-if="props.valuePropositions && index === valuePropositionsPlacementIndex" class="order-1 mb-8 mt-4 w-full sm:mb-12" :data="props.valuePropositions" />-->
      <NewSearchInfoBanner v-if="index === valuePropositionsPlacementIndex" class="order-1 my-4 w-full" />
    </template>
  </div>
</template>

<script setup lang="ts">
import ProductCard from './ProductCard.vue'
// import ValuePropositions from '~/components/value-propositions/ValuePropositions.vue'
import type { HallProduct } from './hall-product'
import NewSearchInfoBanner from '~/components/try-new-search/NewSearchInfoBanner.vue'

const props = withDefaults(
  defineProps<{
    products: HallProduct[]
    hasSmallerTopPadding?: boolean
    valuePropositions?:
      | {
          title: string
          description: string
          iconName: string | null
        }[]
      | undefined
      | null
  }>(),
  {
    valuePropositions: undefined,
    hasSmallerTopPadding: false,
  }
)

// 1.
const valuePropositionsPlacementIndex = computed(() => {
  if (!props.valuePropositions) {
    return -1
  }

  if (props.products.length >= 8) {
    return 7
  }

  return 5
})
</script>

<style lang="scss" scoped>
/*
  Responsive grid details:

  default: 2 products per row, value propositions only after 8th product. (after card with index 7)
  product-md: 3 products per row, value propositions only after 6th product (after card with index 5)
  xl: 4 products per row, value propositons only after 8th product (after card with index 7)

  There are 4 main operations that need to be performed:
  1. If value propositions are to be displayed, place it as far back as possible in natural ordering. So if the farthest position out of all breakpoints is after 8 products,
      then place it after 8th product. Makes it possibly easier to find in DOM and is more in line with the visual representation.
  2. For each breakpoint set the `order` property in CSS accordingly to the amount of preceding items displayed in grid. If that number is 8, then products 1-8 get
      order 0 to preserve original order and everything from 9 and up gets order 2. Value propositions container gets its dedicated order group of value 1.
  3. First row should have no separator border (elements have border-top: 0).
  4. Row following value propositions should have no separator border (in case of 8 products preceding value propositions in row of 4, cards 9-13 have border-top: 0).
*/

@mixin product-grid($per-row, $value-propositions-after) {
  /* 3. */
  .product-card:nth-child(-n + #{$per-row}) {
    @apply border-t-0;
  }

  .product-grid-with-value-propositions .product-card {
    /* 2. */
    &:nth-child(-n + #{$value-propositions-after}) {
      @apply order-none;
    }

    &:nth-child(n + #{$value-propositions-after + 1}) {
      @apply order-2;
    }

    /* 4. */
    &:nth-child(n + #{$value-propositions-after + 1}):nth-child(-n + #{$value-propositions-after + 1 + $per-row}) {
      @apply border-t-0;
    }
  }
}

// Due to complex nature of the operations, it is more convenient to operate on mutually exclusive responsive groups opposed to mobile-first approach.

// Same as Tailwind `max-product-md` modifier.
@media not all and (min-width: theme('screens.product-md')) {
  @include product-grid($per-row: 2, $value-propositions-after: 8);
}

// Same as Tailwind `product-md:max-xl` modifier.
@media (min-width: theme('screens.product-md')) {
  @media not all and (min-width: theme('screens.xl')) {
    @include product-grid($per-row: 3, $value-propositions-after: 6);
  }
}

// Same as Tailwind `xl` modifier.
@media (min-width: theme('screens.xl')) {
  @include product-grid($per-row: 4, $value-propositions-after: 8);
}

/*
  Since separators are implemented as border-top on each card element, it could be that there are not enough products to complete an entire line.
  For that we create a dummy element filling up rest of the space.

  FIXME(Indrek): This also appears when there is only single row of products (see 3. from above)
*/
.product-grid::after {
  @apply grow border-t border-border-light content-[''];
}
</style>
